import { useState, useEffect, useRef } from 'react';
import InputField from '../../../components/Forms/Input/InputField';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import SelectField from '../../../components/Forms/Input/SelectField';
import { ivaTypeOptions } from '../productData';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import {
  calculateTaxFromTotalAndQty,
  calculateTaxFromUnitCostAndQty,
} from '../../../utils/tax/creditTaxCalculator';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { useEnterToClick } from '../../../hooks/useEnter';
import { useProduct } from '../../../hooks/useProduct';
import { useCurrencyInput } from '../../../hooks/useCurrencyInput';
import { formatCurrency } from '../../../utils/formatCurrency';

interface ProductFormValues {
  productId: string;
  productName: string;
  ivaType: string;
  calculatedTotal: string;
  calculatedQty: string;
  unitCost: string;
  quantity: string;
  ivaTotal: string;
  totalCost: string;
  withIva: string;
  ivaUnit: string;
}

interface ProductOption {
  id: number;
  name: string;
  unit?: string;
}

interface Props {
  onAdd: (product: any) => void;
  productOptions?: ProductOption[];
}

const AddProductForm = ({ onAdd, productOptions }: Props) => {
  const { handlerGetInventoryInfo } = useProduct();

  const saveBtnRef = useRef<HTMLButtonElement>(null);
  useEnterToClick(saveBtnRef, true);

  const [form, setForm] = useState<ProductFormValues>({
    productId: '',
    productName: '',
    ivaType: 'GRAVADO',
    calculatedTotal: '',
    calculatedQty: '',
    unitCost: '',
    quantity: '',
    ivaTotal: '',
    totalCost: '',
    withIva: '',
    ivaUnit: '',
  });

  const {
    form: { id: purchaseId },
  } = useSelector((state: RootState) => state.basePurchase);
  const [isManualUnitCost, setIsManualUnitCost] = useState(false);
  const [isManualQuantity, setIsManualQuantity] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 💵 Igual que en Ventas: campos de dinero con separador de miles en vivo
  // (966.2 -> "966.20", 1091.806 -> "1,091.81"), en vez de un <input
  // type="number"> mostrando el número crudo tal cual lo da el cálculo.
  const calculatedTotalInput = useCurrencyInput(form.calculatedTotal, (value, isEmpty) => {
    setForm((prev) => ({ ...prev, calculatedTotal: isEmpty ? '' : String(value) }));
  });

  const unitCostInput = useCurrencyInput(form.unitCost, (value, isEmpty) => {
    setIsManualUnitCost(true);
    setForm((prev) => ({ ...prev, unitCost: isEmpty ? '' : String(value) }));
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // 👉 Si el usuario edita unitCost, lo marcamos como manual
    if (name === 'unitCost') {
      setIsManualUnitCost(true);
    }

    // 👉 Si edita cantidad manual, borra la cantidad calculada
    if (name === 'quantity') {
      setIsManualQuantity(true);
      setForm((prev) => ({
        ...prev,
        quantity: value,
        calculatedQty: '',
      }));
      return;
    }

    // 👉 Si edita cantidad calculadora, borra la cantidad manual
    if (name === 'calculatedQty') {
      setIsManualQuantity(false); // el usuario volvió al modo automático
      setForm((prev) => ({
        ...prev,
        calculatedQty: value,
        quantity: '',
      }));
      return;
    }

    // 👉 Para los demás campos, se actualiza normalmente
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    // 🟡 Cálculo basado en Costo Total + Cantidad (Calculadora)
    if (
      form.calculatedTotal &&
      form.calculatedQty &&
      !form.quantity &&
      !isManualUnitCost &&
      !isManualQuantity
    ) {
      const total = parseFloat(form.calculatedTotal);
      const qty = parseFloat(form.calculatedQty);

      if (total > 0 && qty > 0) {
        const { unitCost, unitCostWithIva, ivaTotal, totalCost, ivaUnit } =
          calculateTaxFromTotalAndQty(total, qty);

        setForm((prev) => ({
          ...prev,
          unitCost: unitCost.toString(),
          withIva: unitCostWithIva.toString(),
          ivaUnit: ivaUnit.toString(),
          ivaTotal: ivaTotal.toString(),
          totalCost: totalCost.toString(),
        }));
      }
    }

    // 🟢 Cálculo extendido: basado en Costo Unitario + Cantidad (manual)
    if (form.unitCost && (form.quantity || form.calculatedQty)) {
      const unitCost = parseFloat(form.unitCost);
      const qty = parseFloat(
        isManualQuantity ? form.quantity : form.calculatedQty
      );

      if (!isNaN(unitCost) && !isNaN(qty) && unitCost > 0 && qty > 0) {
        const { unitCostWithIva, ivaTotal, totalCost, ivaUnit } =
          calculateTaxFromUnitCostAndQty(unitCost, qty);

        setForm((prev) => ({
          ...prev,
          totalCost: totalCost.toString(),
          withIva: unitCostWithIva.toString(),
          ivaUnit: ivaUnit.toString(),
          ivaTotal: ivaTotal.toString(),
        }));
      }
    }
  }, [
    form.calculatedTotal,
    form.calculatedQty,
    form.unitCost,
    form.quantity,
    isManualUnitCost,
    isManualQuantity,
  ]);

  const resetForm = () => {
    setForm({
      productId: '',
      productName: '',
      ivaType: 'GRAVADO',
      calculatedTotal: '',
      calculatedQty: '',
      unitCost: '',
      quantity: '',
      ivaTotal: '',
      totalCost: '',
      withIva: '',
      ivaUnit: '',
    });
    setIsManualUnitCost(false);
    setIsManualQuantity(false);
    setErrors({});
  };

  const handleSave = () => {
    const newErrors: typeof errors = {};

    if (!form.productId) newErrors.productId = 'Producto requerido';
    if (!form.unitCost) newErrors.unitCost = 'Costo unitario requerido';
    if (!form.quantity && !form.calculatedQty)
      newErrors.quantity = 'Cantidad requerida';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const newProduct = {
      purchaseId,
      productId: Number(form.productId),
      quantity: Number(form.quantity || form.calculatedQty),
      unitCost: Number(form.unitCost),
      taxType: form.ivaType === 'GRAVADO' ? 'TAXED' : 'EXEMPT',
    };

    onAdd(newProduct);
    resetForm();
  };

  const [inventoryInfo, setInventoryInfo] = useState<{
    cost?: number;
    stock?: number;
  } | null>(null);

  const handleProductSelect = async (option: any) => {
    setForm((prev) => ({
      ...prev,
      productId: option.id,
      productName: option.name,
    }));

    const inventory = await handlerGetInventoryInfo(option.id);

    setInventoryInfo(inventory);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SimpleSearchSelect
          label="Producto"
          name="productId"
          iconKey="category"
          options={productOptions}
          selectedValue={form.productId ? Number(form.productId) : null}
          onSelect={handleProductSelect}
          error={errors.productId}
          required
        />

        <SelectField
          label="Tipo"
          name="ivaType"
          iconKey="list"
          value={form.ivaType}
          onChange={() => {}}
          options={ivaTypeOptions}
          disabled
          required
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        <InputField
          label="Costo Total (Calculadora)"
          name="calculatedTotal"
          iconKey="dollar"
          type="text"
          inputMode="decimal"
          value={calculatedTotalInput.displayValue}
          onFocus={calculatedTotalInput.onFocus}
          onBlur={calculatedTotalInput.onBlur}
          onKeyDown={calculatedTotalInput.onKeyDown}
          onChange={(e) => calculatedTotalInput.onChangeText(e.target.value)}
        />
        <InputField
          label="Cantidad (Calculadora)"
          name="calculatedQty"
          iconKey="product"
          type="number"
          value={form.calculatedQty}
          onChange={handleChange}
        />
        <InputField
          label="Costo Unitario"
          name="unitCost"
          iconKey="dollar"
          type="text"
          inputMode="decimal"
          value={unitCostInput.displayValue}
          onFocus={unitCostInput.onFocus}
          onBlur={unitCostInput.onBlur}
          onKeyDown={unitCostInput.onKeyDown}
          onChange={(e) => unitCostInput.onChangeText(e.target.value)}
          error={errors.unitCost}
          required
        />
        <InputField
          label="Cantidad"
          name="quantity"
          iconKey="product"
          type="number"
          value={form.quantity}
          onChange={handleChange}
          error={errors.quantity}
        />
        <InputField
          label="IVA Total"
          name="ivaTotal"
          iconKey="dollar"
          type="text"
          value={form.ivaTotal ? formatCurrency(form.ivaTotal) : ''}
          onChange={() => {}}
          error={errors.ivaTotal}
          disabled
        />
        <InputField
          label="Costo Total"
          name="totalCost"
          iconKey="dollar"
          type="text"
          value={form.totalCost ? formatCurrency(form.totalCost) : ''}
          onChange={() => {}}
          error={errors.totalCost}
          disabled
        />
      </div>

      {inventoryInfo && (
        <div
          className="
          text-[11px]

          text-body

          dark:text-bodydark1
        "
        >
          Stock:
          <strong className="mx-1 text-black dark:text-white">
            {inventoryInfo.stock ?? 0}
          </strong>
          •
          <span className="ml-2">
            Último costo:
            <strong className="ml-1 text-black dark:text-white">
              ${Number(inventoryInfo.cost ?? 0).toFixed(2)}
            </strong>
          </span>
        </div>
      )}

      <div className="text-gray-600 space-y-1 text-sm dark:text-white">
        <p>CON IVA: $ {formatCurrency(form.withIva || 0)}</p>
        <p>IVA UNITARIO: $ {formatCurrency(form.ivaUnit || 0)}</p>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          ref={saveBtnRef}
          onClick={handleSave}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="save" className="h-5 w-5" />
          Guardar
        </button>
        <button
          type="button"
          onClick={resetForm}
          className="inline-flex items-center gap-2 rounded-md bg-[#DC3545] px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-opacity-90"
        >
          <SvgIcon name="cancel" className="h-5 w-5" />
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default AddProductForm;
